import { useState } from 'react'
import { motion } from 'motion/react'
import { GraduationCap, Plane, Route, BadgeCheck } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

const STEP_ICONS = [GraduationCap, Plane, Route, BadgeCheck]

function FrancescaPortrait() {
  const { t } = useLanguage()
  const [missing, setMissing] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: -2 }}
      whileInView={{ opacity: 1, y: 0, rotate: -2 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="relative z-10 mx-auto aspect-[3/4] w-64 sm:w-72"
    >
      {missing ? (
        <div className="flex h-full w-full flex-col items-center justify-center rounded-t-full bg-ink text-cream shadow-2xl shadow-ink/25">
          <span className="font-display text-7xl font-light italic">FZ</span>
          <span className="mt-4 px-8 text-center text-xs uppercase tracking-[0.2em] text-cream/60">
            {t.about.portraitMissing}
          </span>
        </div>
      ) : (
        <img
          src="/images/francesca.jpg"
          alt={t.about.portraitAlt}
          onError={() => setMissing(true)}
          className="h-full w-full rounded-t-full object-cover object-top shadow-2xl shadow-ink/25"
          loading="lazy"
        />
      )}
    </motion.div>
  )
}

export default function AboutSection() {
  const { t } = useLanguage()

  const steps = t.about.steps.map((step, i) => ({ ...step, icon: STEP_ICONS[i] }))

  return (
    <section id="chi-sono" className="relative scroll-mt-24 overflow-hidden pb-12 pt-24 md:pb-16 md:pt-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="lg:col-span-7"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">
            {t.about.eyebrow}
          </p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-tight text-ink md:text-6xl">
            {t.about.titleBefore}{' '}
            <span className="italic text-terracotta">{t.about.titleAccent}</span>
          </h2>

          <div className="mt-8 max-w-2xl space-y-5 text-lg leading-relaxed text-ink/75">
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
            <p>
              {t.about.p3Before}{' '}
              <span className="font-display italic text-ink">
                {t.about.p3Brand}
              </span>{' '}
              {t.about.p3After}
            </p>
          </div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <FrancescaPortrait />

          <motion.div
            initial={{ opacity: 0, y: 32, rotate: 6 }}
            whileInView={{ opacity: 1, y: 0, rotate: 3 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            whileHover={{ rotate: 12, transition: { type: 'spring', stiffness: 260, damping: 14, delay: 0 } }}
            className="absolute -bottom-8 left-1/2 z-20 -ml-16 sm:-ml-20 lg:-left-8 lg:ml-0"
            aria-label={t.about.badgeAria}
          >
            <div className="relative h-32 w-32 text-cream drop-shadow-xl sm:h-40 sm:w-40">
              <svg viewBox="0 0 100 100" className="h-full w-full">
                <defs>
                  <path
                    id="badge-text-circle"
                    d="M 50 50 m -34 0 a 34 34 0 1 1 68 0 a 34 34 0 1 1 -68 0"
                  />
                </defs>
                <circle cx="50" cy="50" r="50" className="fill-terracotta" />
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  fill="none"
                  strokeWidth="1"
                  strokeDasharray="2 3"
                  className="stroke-cream/60"
                />
                <text className="fill-cream" fontSize="9" letterSpacing="1.4">
                  <textPath href="#badge-text-circle">
                    TRAVEL DESIGNER • CERTIFICATA •
                  </textPath>
                </text>
              </svg>
              <BadgeCheck
                className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 text-cream sm:h-10 sm:w-10"
                strokeWidth={1.6}
              />
            </div>
          </motion.div>

          <div
            className="route-dash absolute -right-6 top-1/3 hidden w-40 text-terracotta/70 lg:block"
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-7xl px-4 sm:px-6 lg:px-8">
        <ol className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <motion.li
              key={step.place}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex items-start gap-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-ink text-ink">
                <step.icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-display text-lg font-semibold text-ink">
                  {step.place}
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-ink/65">
                  {step.text}
                </span>
              </span>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  )
}
